import React from 'react';
import { Link } from 'react-router-dom';
import { FooterSection } from '../components/landing';
import { useSEO } from '../hooks/useSEO';

const FeaturesPage: React.FC = () => {
  useSEO({
    title: 'Features — AI-Powered Sprint Retrospectives',
    description:
      'Discover all Retrosive features: AI retrospective summary, theme detection, action item assignment, real-time collaboration, anonymous mode, 12+ templates, and more.',
    canonical: '/features',
  });
  const features = [
    {
      category: 'AI Retrospective Co-Facilitator',
      icon: '✨',
      color: 'from-orange-500 to-amber-400',
      items: [
        {
          title: 'Automatic AI Summary',
          description: 'One click generates an executive-ready summary with themes, wins, blockers, and next steps.',
          icon: '🤖',
        },
        {
          title: 'Theme Detection',
          description: 'AI groups related feedback and surfaces the top recurring topics across your team\'s notes.',
          icon: '🔍',
        },
        {
          title: 'Action Item Assignment',
          description: 'AI identifies action items from discussions and suggests owners — so nothing falls through the cracks.',
          icon: '🎯',
        },
        {
          title: 'Cross-Sprint Pattern Analysis',
          description: 'See which problems keep coming back. AI tracks patterns across all your past retrospectives.',
          icon: '📈',
        },
      ],
    },
    {
      category: 'Real-time Collaboration',
      icon: '⚡',
      color: 'from-yellow-400 to-orange-500',
      items: [
        {
          title: 'Instant Sync',
          description: 'Every change is synchronized instantly across all participants in real-time.',
          icon: '🔄',
        },
        {
          title: 'Live Cursors',
          description: 'See where your teammates are working in real-time with live cursor tracking.',
          icon: '🖱️',
        },
        {
          title: 'Presence Indicators',
          description: 'Know who is online and actively participating in your retrospective.',
          icon: '👥',
        },
        {
          title: 'Real-time Voting',
          description: 'Watch vote counts update instantly as team members express their opinions.',
          icon: '📊',
        },
      ],
    },
    {
      category: 'Facilitator Controls',
      icon: '🎛️',
      color: 'from-orange-400 to-pink-500',
      items: [
        {
          title: 'Phase Management',
          description: 'Guide your team through writing, grouping, voting, and discussion phases.',
          icon: '📋',
        },
        {
          title: 'Private Writing Mode',
          description: 'Hide cards until you reveal them to prevent groupthink and encourage honest feedback.',
          icon: '🔒',
        },
        {
          title: 'Anonymous Mode',
          description: 'Let team members share feedback anonymously for more candid conversations.',
          icon: '🎭',
        },
        {
          title: 'Session Timer',
          description: 'Keep your retrospectives focused with built-in timer controls.',
          icon: '⏱️',
        },
      ],
    },
    {
      category: 'Board Templates',
      icon: '📋',
      color: 'from-amber-400 to-blue-500',
      items: [
        {
          title: '12+ Templates',
          description: 'Choose from professionally designed templates for every retrospective format.',
          icon: '🎨',
        },
        {
          title: 'Custom Boards',
          description: 'Create your own board layouts with custom columns and colors.',
          icon: '✨',
        },
        {
          title: 'Quick Start',
          description: 'Get started in seconds with pre-configured board setups.',
          icon: '🚀',
        },
        {
          title: 'Save as Template',
          description: 'Save your custom boards as templates for future use.',
          icon: '💾',
        },
      ],
    },
    {
      category: 'Team Management',
      icon: '👥',
      color: 'from-green-400 to-emerald-500',
      items: [
        {
          title: 'Team Workspaces',
          description: 'Organize boards by teams for better structure and access control.',
          icon: '🏢',
        },
        {
          title: 'Invite Links',
          description: 'Share simple invite links to add team members instantly.',
          icon: '🔗',
        },
        {
          title: 'Member Management',
          description: 'Add or remove team members and manage permissions.',
          icon: '👤',
        },
        {
          title: 'Board Sharing',
          description: 'Share boards with specific teams or make them accessible via link.',
          icon: '📤',
        },
      ],
    },
    {
      category: 'Security & Privacy',
      icon: '🔐',
      color: 'from-orange-400 to-orange-500',
      items: [
        {
          title: 'Secure Login',
          description: 'Your account is protected with industry-standard security.',
          icon: '🔑',
        },
        {
          title: 'Private by Default',
          description: 'Your retrospective data stays private and only visible to your team.',
          icon: '🛡️',
        },
        {
          title: 'Encrypted Connection',
          description: 'All communication between you and Retrosive is fully encrypted.',
          icon: '🔒',
        },
        {
          title: 'Data Protection',
          description: 'Built with privacy in mind, respecting your data rights.',
          icon: '🌍',
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-orange-800 to-slate-900 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-2 rounded-full bg-white/10 text-white/80 text-sm font-semibold mb-6 backdrop-blur-sm border border-white/20">
            Features
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            AI-powered features for
            <span className="block bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
              retrospectives that drive change
            </span>
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            From AI summaries and pattern detection to real-time collaboration — everything your team needs to run retrospectives that actually improve how you work.
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {features.map((category, categoryIndex) => (
              <div key={category.category}>
                <div className={`flex items-center gap-4 mb-12 ${categoryIndex % 2 === 1 ? 'justify-end' : ''}`}>
                  <div className={`inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r ${category.color} text-white`}>
                    <span className="text-2xl">{category.icon}</span>
                    <h2 className="text-2xl font-bold">{category.category}</h2>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {category.items.map((item) => (
                    <div
                      key={item.title}
                      className="group bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-xl hover:border-transparent transition-all duration-300 hover:-translate-y-1"
                    >
                      <div className="text-4xl mb-4 group-hover:scale-110 transition-transform">
                        {item.icon}
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {item.title}
                      </h3>
                      <p className="text-gray-600">
                        {item.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-r from-orange-500 to-amber-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to get started?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Start running better retrospectives with your team today.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-orange-500 font-bold text-lg rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300"
          >
            Start Free Now
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>
      </section>

      <FooterSection />
    </div>
  );
};

export default FeaturesPage;

